/**
 * Settings Page
 */
import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  User, Phone, MapPin, Building, Award, Lock, Eye, EyeOff, Loader2, Check,
  Download, FileText, AlertTriangle, Trash2, Briefcase, XCircle, Camera, Upload, Bell
} from "lucide-react";
import SwissPostAutocomplete from "../components/SwissPostAutocomplete";
import AddressAutocomplete from "../components/AddressAutocomplete";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Swiss Cantons
const SWISS_CANTONS = [
  { code: "AG", name: "Aargau" }, { code: "AI", name: "Appenzell Innerrhoden" },
  { code: "AR", name: "Appenzell Ausserrhoden" }, { code: "BE", name: "Bern" },
  { code: "BL", name: "Basel-Landschaft" }, { code: "BS", name: "Basel-Stadt" },
  { code: "FR", name: "Fribourg" }, { code: "GE", name: "Genève" },
  { code: "GL", name: "Glarus" }, { code: "GR", name: "Graubünden" },
  { code: "JU", name: "Jura" }, { code: "LU", name: "Luzern" },
  { code: "NE", name: "Neuchâtel" }, { code: "NW", name: "Nidwalden" },
  { code: "OW", name: "Obwalden" }, { code: "SG", name: "St. Gallen" },
  { code: "SH", name: "Schaffhausen" }, { code: "SO", name: "Solothurn" },
  { code: "SZ", name: "Schwyz" }, { code: "TG", name: "Thurgau" },
  { code: "TI", name: "Ticino" }, { code: "UR", name: "Uri" },
  { code: "VD", name: "Vaud" }, { code: "VS", name: "Valais" },
  { code: "ZG", name: "Zug" }, { code: "ZH", name: "Zürich" }
];

const FREQUENCY_LABELS = {
  "as_needed": "Bei Bedarf",
  "weekly": "Wöchentlich",
  "2x_weekly": "2x wöchentlich",
  "3x_weekly": "3x wöchentlich",
  "max_2x_weekly": "Max. 2x wöchentlich",
  "daily": "Täglich"
};

const SettingsPage = ({ useAuth, useLanguage }) => {
  const { user, setUser, logout } = useAuth();
  const { t, setLanguage: setUILanguage, language: currentUILang } = useLanguage();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [providerLoading, setProviderLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [categories, setCategories] = useState([]);
  const [providerProfile, setProviderProfile] = useState(null);
  const [profilePictureLoading, setProfilePictureLoading] = useState(false);
  const [documentLoading, setDocumentLoading] = useState(false);
  const [documents, setDocuments] = useState([]);
  
  const [formData, setFormData] = useState({
    name: user?.name || '',
    phone: user?.phone || '',
    city: user?.location?.city || ''
  });
  
  const [providerData, setProviderData] = useState({
    company_name: '',
    address: '',
    postal_code: '',
    canton: '',
    selected_categories: [],
    certifications: '',
    description: ''
  });
  
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  
  // Notification preferences state
  const [notifPrefs, setNotifPrefs] = useState({
    categories: {},
    quiet_hours_start: "21:00",
    quiet_hours_end: "07:30",
    language: "de",
    max_per_day: 2
  });
  const [notifCategories, setNotifCategories] = useState([]);
  const [notifLoading, setNotifLoading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/categories`).then(res => setCategories(res.data)).catch(console.error);
    
    // Fetch fresh user data to populate form
    axios.get(`${API}/auth/me`, { withCredentials: true })
      .then(res => {
        const userData = res.data;
        setFormData({
          name: userData.name || '',
          phone: userData.phone || '',
          city: userData.location?.city || ''
        });
        
        if (userData.role === 'provider') {
          // Fetch provider profile
          axios.get(`${API}/users/${userData.user_id}`, { withCredentials: true })
            .then(provRes => {
              const profile = provRes.data.provider_profile || {};
              const location = provRes.data.location || {};
              setProviderProfile(profile);
              setProviderData({
                company_name: provRes.data.company_name || '',
                address: location.address || provRes.data.address || '',
                postal_code: location.postal_code || provRes.data.postal_code || '',
                canton: location.canton || provRes.data.canton || '',
                selected_categories: profile.categories || [],
                certifications: profile.certifications?.join(', ') || '',
                description: profile.description || ''
              });
            })
            .catch(console.error);
          
          // Fetch documents
          axios.get(`${API}/users/documents`, { withCredentials: true })
            .then(res => setDocuments(res.data))
            .catch(console.error);
        }
      })
      .catch(console.error);
    
    // Fetch notification preferences
    axios.get(`${API}/notifications/preferences`, { withCredentials: true })
      .then(res => {
        setNotifPrefs({
          categories: res.data.categories || {},
          quiet_hours_start: res.data.quiet_hours_start || "21:00",
          quiet_hours_end: res.data.quiet_hours_end || "07:30",
          language: res.data.language || currentUILang || "de",
          max_per_day: res.data.max_per_day || 2
        });
        setNotifCategories(res.data.available_categories || []);
      })
      .catch(console.error);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.put(`${API}/users/profile`, {
        name: formData.name,
        phone: formData.phone,
        location: { city: formData.city, country: 'CH' }
      }, { withCredentials: true });
      setUser({ ...user, ...formData, location: { city: formData.city, country: 'CH' } });
      toast.success('Profil aktualisiert!');
    } catch (error) {
      toast.error('Fehler beim Speichern');
    } finally {
      setLoading(false);
    }
  };

  const handleNotifSave = async () => {
    setNotifLoading(true);
    try {
      await axios.put(`${API}/notifications/preferences`, notifPrefs, { withCredentials: true });
      // Also sync UI language with notification language preference
      if (notifPrefs.language) {
        setUILanguage(notifPrefs.language);
      }
      toast.success('Benachrichtigungseinstellungen gespeichert!');
    } catch (error) {
      toast.error('Fehler beim Speichern');
    } finally {
      setNotifLoading(false);
    }
  };

  const toggleNotifCategory = (categoryId) => {
    setNotifPrefs(prev => ({
      ...prev,
      categories: {
        ...prev.categories,
        [categoryId]: !prev.categories[categoryId]
      }
    }));
  };

  const handleProviderSubmit = async (e) => {
    e.preventDefault();
    setProviderLoading(true);
    try {
      await axios.put(`${API}/users/company-info`, {
        company_name: providerData.company_name,
        address: providerData.address,
        postal_code: providerData.postal_code,
        canton: providerData.canton,
        categories: providerData.selected_categories,
        certifications: providerData.certifications.split(',').map(c => c.trim()).filter(Boolean),
        description: providerData.description
      }, { withCredentials: true });
      toast.success('Firmendaten aktualisiert!');
    } catch (error) {
      toast.error('Fehler beim Speichern');
    } finally {
      setProviderLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('Passwörter stimmen nicht überein');
      return;
    }
    if (passwordData.newPassword.length < 6) {
      toast.error('Passwort muss mindestens 6 Zeichen haben');
      return;
    }
    setPasswordLoading(true);
    try {
      await axios.put(`${API}/users/password`, {
        current_password: passwordData.currentPassword,
        new_password: passwordData.newPassword
      }, { withCredentials: true });
      toast.success('Passwort geändert!');
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Ändern des Passworts');
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleDownloadData = async () => {
    setDownloadLoading(true);
    try {
      const res = await axios.get(`${API}/users/my-data`, { withCredentials: true });
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `infirmly-data-${user.user_id}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Daten heruntergeladen');
    } catch (error) {
      toast.error('Fehler beim Herunterladen');
    } finally {
      setDownloadLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    setDeleteLoading(true);
    try {
      await axios.delete(`${API}/users/account`, { withCredentials: true });
      toast.success('Konto gelöscht');
      logout();
      navigate('/');
    } catch (error) {
      toast.error('Fehler beim Löschen');
    } finally {
      setDeleteLoading(false);
      setShowDeleteConfirm(false);
    }
  };

  const toggleCategory = (categoryId) => {
    setProviderData(prev => ({
      ...prev,
      selected_categories: prev.selected_categories.includes(categoryId)
        ? prev.selected_categories.filter(id => id !== categoryId)
        : [...prev.selected_categories, categoryId]
    }));
  };

  const handleProfilePictureUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error('Bitte wählen Sie ein Bild aus');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Bild zu gross (max. 5MB)');
      return;
    }
    
    setProfilePictureLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await axios.post(`${API}/users/profile-picture`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true
      });
      
      if (res.data.success) {
        setUser({ ...user, profile_picture: res.data.profile_picture });
        toast.success('Profilbild aktualisiert!');
      }
    } catch (error) {
      toast.error('Fehler beim Hochladen');
    } finally {
      setProfilePictureLoading(false);
    }
  };

  const handleDocumentUpload = async (e, documentType) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Datei zu gross (max. 10MB)');
      return;
    }
    
    setDocumentLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('document_type', documentType);
      
      const res = await axios.post(`${API}/users/documents`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true
      });
      
      if (res.data.success) {
        setDocuments([...documents, res.data]);
        toast.success('Dokument hochgeladen!');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Hochladen');
    } finally {
      setDocumentLoading(false);
    }
  };

  const handleDeleteDocument = async (docId) => {
    try {
      await axios.delete(`${API}/users/documents/${docId}`, { withCredentials: true });
      setDocuments(documents.filter(d => d.doc_id !== docId));
      toast.success('Dokument gelöscht');
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  // Client Settings
  if (user?.role === 'client') {
    return (
      <div className="max-w-2xl mx-auto" data-testid="settings-page">
        <h1 className="text-2xl font-bold tracking-tight mb-6" style={{ fontFamily: 'Chivo, sans-serif' }}>
          {t('settings')}
        </h1>

        <Tabs defaultValue="profile" className="space-y-6">
          <TabsList className="grid w-full grid-cols-4 h-12">
            <TabsTrigger value="profile" className="data-[state=active]:bg-[#0044CC] data-[state=active]:text-white">
              <User className="w-4 h-4 mr-2" /> Profil
            </TabsTrigger>
            <TabsTrigger value="notifications" className="data-[state=active]:bg-[#0044CC] data-[state=active]:text-white">
              <Bell className="w-4 h-4 mr-2" /> Benachrichtigungen
            </TabsTrigger>
            <TabsTrigger value="security" className="data-[state=active]:bg-[#0044CC] data-[state=active]:text-white">
              <Lock className="w-4 h-4 mr-2" /> Sicherheit
            </TabsTrigger>
            <TabsTrigger value="data" className="data-[state=active]:bg-[#0044CC] data-[state=active]:text-white">
              <FileText className="w-4 h-4 mr-2" /> Daten
            </TabsTrigger>
          </TabsList>

          <TabsContent value="profile">
            <Card className="border-gray-200">
              <CardHeader>
                <CardTitle>Persönliche Informationen</CardTitle>
                <CardDescription>Aktualisieren Sie Ihre Kontaktdaten</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="name">Name</Label>
                    <Input id="name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="mt-1" data-testid="name-input" />
                  </div>
                  <div>
                    <Label htmlFor="phone">Telefon</Label>
                    <Input id="phone" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="mt-1" data-testid="phone-input" />
                  </div>
                  <div>
                    <Label htmlFor="city">Stadt</Label>
                    <Input id="city" value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      className="mt-1" data-testid="city-input" />
                  </div>
                  <Button type="submit" className="w-full bg-[#0044CC] hover:bg-[#003399] rounded-full"
                    disabled={loading} data-testid="save-profile-btn">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4 mr-2" /> Speichern</>}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="notifications" data-testid="client-notifications-tab">
            <div className="space-y-4">
              <Card className="border-gray-200">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Bell className="w-5 h-5 text-[#0044CC]" /> Benachrichtigungskategorien
                  </CardTitle>
                  <CardDescription>Wählen Sie, welche Benachrichtigungen Sie erhalten möchten</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {notifCategories.map(cat => (
                    <div key={cat.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex-1">
                        <p className="font-medium text-sm">{cat.label || cat.name_de}</p>
                        {cat.description && (
                          <p className="text-xs text-gray-500 mt-0.5">{cat.description}</p>
                        )}
                      </div>
                      <button
                        data-testid={`notif-toggle-${cat.id}`}
                        onClick={() => toggleNotifCategory(cat.id)}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          notifPrefs.categories[cat.id] ? 'bg-[#0044CC]' : 'bg-gray-200'
                        }`}
                      >
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                          notifPrefs.categories[cat.id] ? 'translate-x-6' : 'translate-x-1'
                        }`} />
                      </button>
                    </div>
                  ))}
                </CardContent>
              </Card>

              <Card className="border-gray-200">
                <CardHeader>
                  <CardTitle className="text-base">Ruhezeiten &amp; Häufigkeit</CardTitle>
                  <CardDescription>Keine Benachrichtigungen in dieser Zeit</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm">Ruhephase von</Label>
                      <Input
                        type="time"
                        value={notifPrefs.quiet_hours_start}
                        onChange={e => setNotifPrefs(p => ({ ...p, quiet_hours_start: e.target.value }))}
                        className="mt-1"
                        data-testid="quiet-hours-start"
                      />
                    </div>
                    <div>
                      <Label className="text-sm">Ruhephase bis</Label>
                      <Input
                        type="time"
                        value={notifPrefs.quiet_hours_end}
                        onChange={e => setNotifPrefs(p => ({ ...p, quiet_hours_end: e.target.value }))}
                        className="mt-1"
                        data-testid="quiet-hours-end"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm">Max. Benachrichtigungen pro Tag</Label>
                    <Select
                      value={String(notifPrefs.max_per_day)}
                      onValueChange={v => setNotifPrefs(p => ({ ...p, max_per_day: Number(v) }))}
                    >
                      <SelectTrigger className="mt-1" data-testid="max-per-day-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[1,2,3,4,5].map(n => (
                          <SelectItem key={n} value={String(n)}>{n} pro Tag</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-sm">Bevorzugte Sprache</Label>
                    <Select
                      value={notifPrefs.language}
                      onValueChange={v => setNotifPrefs(p => ({ ...p, language: v }))}
                    >
                      <SelectTrigger className="mt-1" data-testid="language-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="de">Deutsch</SelectItem>
                        <SelectItem value="en">English</SelectItem>
                        <SelectItem value="fr">Français</SelectItem>
                        <SelectItem value="it">Italiano</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </CardContent>
              </Card>

              <Button
                onClick={handleNotifSave}
                disabled={notifLoading}
                className="w-full bg-[#0044CC] hover:bg-[#003399] rounded-full"
                data-testid="save-notif-btn"
              >
                {notifLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                {t('save')}
              </Button>
            </div>
          </TabsContent>

          <TabsContent value="security">
            <Card className="border-gray-200">
              <CardHeader>
                <CardTitle>Passwort ändern</CardTitle>
                <CardDescription>Aktualisieren Sie Ihr Passwort regelmäßig</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handlePasswordChange} className="space-y-4">
                  <div>
                    <Label>Aktuelles Passwort</Label>
                    <div className="relative mt-1">
                      <Input type={showCurrentPassword ? "text" : "password"} value={passwordData.currentPassword}
                        onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                        className="pr-10" data-testid="current-password-input" />
                      <button type="button" onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                        {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <Label>Neues Passwort</Label>
                    <div className="relative mt-1">
                      <Input type={showNewPassword ? "text" : "password"} value={passwordData.newPassword}
                        onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                        className="pr-10" data-testid="new-password-input" />
                      <button type="button" onClick={() => setShowNewPassword(!showNewPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                        {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <Label>Passwort bestätigen</Label>
                    <Input type="password" value={passwordData.confirmPassword}
                      onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                      className="mt-1" data-testid="confirm-password-input" />
                  </div>
                  <Button type="submit" className="w-full bg-[#0044CC] hover:bg-[#003399] rounded-full"
                    disabled={passwordLoading} data-testid="change-password-btn">
                    {passwordLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Passwort ändern'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="data">
            <div className="space-y-4">
              <Card className="border-gray-200">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Download className="w-5 h-5 text-[#0044CC]" /> Meine Daten herunterladen
                  </CardTitle>
                  <CardDescription>Laden Sie eine Kopie Ihrer Daten herunter (DSGVO Art. 15)</CardDescription>
                </CardHeader>
                <CardContent>
                  <Button onClick={handleDownloadData} variant="outline" className="w-full border-[#0044CC] text-[#0044CC] rounded-full"
                    disabled={downloadLoading} data-testid="download-data-btn">
                    {downloadLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}
                    Daten als JSON herunterladen
                  </Button>
                </CardContent>
              </Card>

              <Card className="border-red-200 bg-red-50/50">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-red-600">
                    <AlertTriangle className="w-5 h-5" /> Konto löschen
                  </CardTitle>
                  <CardDescription className="text-red-600/70">Diese Aktion kann nicht rückgängig gemacht werden</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-600 mb-4">
                    Durch das Löschen Ihres Kontos werden alle Ihre Daten, Nachrichten und Aufträge unwiderruflich entfernt.
                  </p>
                  <Button onClick={() => setShowDeleteConfirm(true)} variant="destructive" className="w-full rounded-full"
                    data-testid="delete-account-btn">
                    <Trash2 className="w-4 h-4 mr-2" /> Konto unwiderruflich löschen
                  </Button>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>

        {/* Delete Confirmation Dialog */}
        <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <AlertTriangle className="w-5 h-5" /> Konto wirklich löschen?
              </DialogTitle>
              <DialogDescription>
                Diese Aktion ist endgültig. Alle Ihre Daten werden gelöscht und können nicht wiederhergestellt werden.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setShowDeleteConfirm(false)} className="rounded-full">
                Abbrechen
              </Button>
              <Button variant="destructive" onClick={handleDeleteAccount} disabled={deleteLoading} className="rounded-full"
                data-testid="confirm-delete-btn">
                {deleteLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Ja, Konto löschen'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Provider Settings
  return (
    <div className="max-w-3xl mx-auto" data-testid="settings-page">
      <h1 className="text-2xl font-bold tracking-tight mb-6" style={{ fontFamily: 'Chivo, sans-serif' }}>
        {t('settings')}
      </h1>

      <Tabs defaultValue="profile" className="space-y-6">
        <TabsList className="grid w-full grid-cols-5 h-12">
          <TabsTrigger value="profile" className="data-[state=active]:bg-[#0044CC] data-[state=active]:text-white">
            <User className="w-4 h-4 mr-1 hidden sm:block" /> Profil
          </TabsTrigger>
          <TabsTrigger value="business" className="data-[state=active]:bg-[#0044CC] data-[state=active]:text-white">
            <Building className="w-4 h-4 mr-1 hidden sm:block" /> Beruf
          </TabsTrigger>
          <TabsTrigger value="notifications" className="data-[state=active]:bg-[#0044CC] data-[state=active]:text-white">
            <Bell className="w-4 h-4 mr-1 hidden sm:block" /> Benach.
          </TabsTrigger>
          <TabsTrigger value="security" className="data-[state=active]:bg-[#0044CC] data-[state=active]:text-white">
            <Lock className="w-4 h-4 mr-1 hidden sm:block" /> Sicherheit
          </TabsTrigger>
          <TabsTrigger value="data" className="data-[state=active]:bg-[#0044CC] data-[state=active]:text-white">
            <FileText className="w-4 h-4 mr-1 hidden sm:block" /> Daten
          </TabsTrigger>
        </TabsList>

        {/* Profile Tab */}
        <TabsContent value="profile">
          <Card className="border-gray-200">
            <CardHeader>
              <CardTitle>Persönliche Informationen</CardTitle>
              <CardDescription>Grundlegende Kontaktdaten und Profilbild</CardDescription>
            </CardHeader>
            <CardContent>
              {/* Profile Picture Upload */}
              <div className="flex items-center gap-6 mb-6 pb-6 border-b">
                <div className="relative">
                  <div className="w-24 h-24 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden border-2 border-gray-200">
                    {user?.profile_picture ? (
                      <img src={user.profile_picture} alt="Profil" className="w-full h-full object-cover" />
                    ) : (
                      <User className="w-10 h-10 text-gray-400" />
                    )}
                  </div>
                  <label className="absolute bottom-0 right-0 w-8 h-8 bg-[#0044CC] rounded-full flex items-center justify-center cursor-pointer hover:bg-[#003399] transition-colors">
                    {profilePictureLoading ? (
                      <Loader2 className="w-4 h-4 text-white animate-spin" />
                    ) : (
                      <Camera className="w-4 h-4 text-white" />
                    )}
                    <input 
                      type="file" 
                      accept="image/*" 
                      className="hidden" 
                      onChange={handleProfilePictureUpload}
                      disabled={profilePictureLoading}
                    />
                  </label>
                </div>
                <div>
                  <p className="font-medium">Profilbild</p>
                  <p className="text-sm text-gray-500">JPG, PNG oder GIF (max. 5MB)</p>
                </div>
              </div>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label>Name</Label>
                  <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="mt-1" data-testid="name-input" />
                </div>
                <div>
                  <Label>Telefon</Label>
                  <Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="mt-1" data-testid="phone-input" />
                </div>
                <Button type="submit" className="w-full bg-[#0044CC] hover:bg-[#003399] rounded-full"
                  disabled={loading} data-testid="save-profile-btn">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4 mr-2" /> Speichern</>}
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Business Tab */}
        <TabsContent value="business">
          <Card className="border-gray-200">
            <CardHeader>
              <CardTitle>Berufsprofil</CardTitle>
              <CardDescription>Ihre Firmendaten und Dienstleistungen</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleProviderSubmit} className="space-y-4">
                <div>
                  <Label>Firmenname</Label>
                  <Input value={providerData.company_name}
                    onChange={(e) => setProviderData({ ...providerData, company_name: e.target.value })}
                    className="mt-1" placeholder="z.B. Müller Consulting GmbH" data-testid="company-input" />
                </div>
                <div>
                  <Label>Beschreibung</Label>
                  <Textarea value={providerData.description}
                    onChange={(e) => setProviderData({ ...providerData, description: e.target.value })}
                    className="mt-1 min-h-[100px]" placeholder="Beschreiben Sie Ihre Dienstleistungen..."
                    data-testid="description-input" />
                </div>
                <div>
                  <Label>Standort (PLZ oder Ort)</Label>
                  <SwissPostAutocomplete
                    value={providerData.postal_code ? `${providerData.postal_code} ${providerData.address}` : ''}
                    placeholder="PLZ oder Ort eingeben..."
                    onPlaceSelect={(place) => {
                      if (place) {
                        setProviderData(prev => ({
                          ...prev,
                          postal_code: place.postal_code,
                          address: place.city,
                          canton: place.canton
                        }));
                      }
                    }}
                    className="mt-1"
                  />
                  <p className="text-xs text-gray-500 mt-1">Geben Sie PLZ oder Ort ein für Autocomplete</p>
                </div>
                <div>
                  <Label>Strasse & Hausnummer</Label>
                  <AddressAutocomplete
                    value={providerData.address}
                    onChange={(val) => setProviderData({ ...providerData, address: val })}
                    onSelect={(suggestion) => setProviderData({ ...providerData, address: suggestion.display_name })}
                    placeholder="Strasse, Hausnummer eingeben..."
                    className="mt-1"
                    data-testid="address-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>PLZ</Label>
                    <Input value={providerData.postal_code}
                      onChange={(e) => setProviderData({ ...providerData, postal_code: e.target.value })}
                      className="mt-1" data-testid="postal-input" />
                  </div>
                  <div>
                    <Label>Kanton</Label>
                    <Select value={providerData.canton} onValueChange={(v) => setProviderData({ ...providerData, canton: v })}>
                      <SelectTrigger className="mt-1" data-testid="canton-select">
                        <SelectValue placeholder="Kanton wählen" />
                      </SelectTrigger>
                      <SelectContent>
                        {SWISS_CANTONS.map((c) => (
                          <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>Kategorien</Label>
                  <ScrollArea className="h-40 border rounded-lg p-3 mt-1">
                    <div className="space-y-2">
                      {categories.map((cat) => {
                        const isSelected = providerData.selected_categories.includes(cat.id);
                        return (
                          <div key={cat.id}
                            className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-all ${isSelected ? 'bg-blue-50 border border-[#0044CC]' : 'bg-gray-50 hover:bg-gray-100'}`}
                            onClick={() => toggleCategory(cat.id)} data-testid={`category-${cat.id}`}>
                            <Checkbox checked={isSelected} className="pointer-events-none" />
                            <span className={`text-sm ${isSelected ? 'text-[#0044CC] font-medium' : 'text-gray-700'}`}>
                              {cat[`name_${currentUILang}`] || cat.name}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </ScrollArea>
                </div>
                <div>
                  <Label>Zertifizierungen</Label>
                  <Textarea value={providerData.certifications}
                    onChange={(e) => setProviderData({ ...providerData, certifications: e.target.value })}
                    className="mt-1" placeholder="CPA, MBA, Fachausweise (kommagetrennt)" data-testid="certifications-input" />
                </div>
                
                {/* Document Upload Section */}
                <Separator className="my-4" />
                <div>
                  <Label className="text-base font-semibold">Firmendokumente</Label>
                  <p className="text-sm text-gray-500 mt-1 mb-4">
                    Laden Sie Ihre Firmenunterlagen hoch für die Verifizierung
                  </p>
                  
                  {/* Uploaded Documents */}
                  {documents.length > 0 && (
                    <div className="space-y-2 mb-4">
                      {documents.map((doc) => (
                        <div key={doc.doc_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <FileText className="w-5 h-5 text-[#0044CC]" />
                            <div>
                              <p className="text-sm font-medium">{doc.filename}</p>
                              <p className="text-xs text-gray-500">
                                {doc.document_type === 'company_registration' && 'Handelsregister'}
                                {doc.document_type === 'professional_certificate' && 'Fachausweis'}
                                {doc.document_type === 'id_document' && 'Ausweis'}
                                {doc.document_type === 'other' && 'Sonstiges'}
                              </p>
                            </div>
                          </div>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleDeleteDocument(doc.doc_id)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Upload Buttons */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="relative">
                      <input 
                        type="file" 
                        id="doc-company" 
                        className="hidden" 
                        onChange={(e) => handleDocumentUpload(e, 'company_registration')}
                        disabled={documentLoading}
                      />
                      <label 
                        htmlFor="doc-company" 
                        className="flex items-center justify-center gap-2 p-3 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-[#0044CC] hover:bg-blue-50 transition-colors"
                      >
                        <Upload className="w-4 h-4 text-gray-500" />
                        <span className="text-sm text-gray-600">Handelsregister</span>
                      </label>
                    </div>
                    <div className="relative">
                      <input 
                        type="file" 
                        id="doc-cert" 
                        className="hidden" 
                        onChange={(e) => handleDocumentUpload(e, 'professional_certificate')}
                        disabled={documentLoading}
                      />
                      <label 
                        htmlFor="doc-cert" 
                        className="flex items-center justify-center gap-2 p-3 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-[#0044CC] hover:bg-blue-50 transition-colors"
                      >
                        <Upload className="w-4 h-4 text-gray-500" />
                        <span className="text-sm text-gray-600">Fachausweis</span>
                      </label>
                    </div>
                    <div className="relative">
                      <input 
                        type="file" 
                        id="doc-id" 
                        className="hidden" 
                        onChange={(e) => handleDocumentUpload(e, 'id_document')}
                        disabled={documentLoading}
                      />
                      <label 
                        htmlFor="doc-id" 
                        className="flex items-center justify-center gap-2 p-3 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-[#0044CC] hover:bg-blue-50 transition-colors"
                      >
                        <Upload className="w-4 h-4 text-gray-500" />
                        <span className="text-sm text-gray-600">Ausweis</span>
                      </label>
                    </div>
                    <div className="relative">
                      <input 
                        type="file" 
                        id="doc-other" 
                        className="hidden" 
                        onChange={(e) => handleDocumentUpload(e, 'other')}
                        disabled={documentLoading}
                      />
                      <label 
                        htmlFor="doc-other" 
                        className="flex items-center justify-center gap-2 p-3 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-[#0044CC] hover:bg-blue-50 transition-colors"
                      >
                        <Upload className="w-4 h-4 text-gray-500" />
                        <span className="text-sm text-gray-600">Sonstiges</span>
                      </label>
                    </div>
                  </div>
                  {documentLoading && (
                    <div className="flex items-center justify-center gap-2 mt-3 text-sm text-gray-500">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Dokument wird hochgeladen...
                    </div>
                  )}
                  <p className="text-xs text-gray-400 mt-3">
                    Unterstützte Formate: PDF, JPG, PNG (max. 10MB)
                  </p>
                </div>
                
                <Button type="submit" className="w-full bg-[#0044CC] hover:bg-[#003399] rounded-full"
                  disabled={providerLoading} data-testid="save-business-btn">
                  {providerLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4 mr-2" /> Speichern</>}
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Notifications Tab */}
        <TabsContent value="notifications" data-testid="provider-notifications-tab">
          <div className="space-y-4">
            <Card className="border-gray-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Bell className="w-5 h-5 text-[#0044CC]" /> Benachrichtigungskategorien
                </CardTitle>
                <CardDescription>Wählen Sie, welche Benachrichtigungen Sie erhalten möchten</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {notifCategories.map(cat => (
                  <div key={cat.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <p className="font-medium text-sm">{cat.label || cat.name_de}</p>
                      {cat.description && (
                        <p className="text-xs text-gray-500 mt-0.5">{cat.description}</p>
                      )}
                    </div>
                    <button
                      data-testid={`provider-notif-toggle-${cat.id}`}
                      onClick={() => toggleNotifCategory(cat.id)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        notifPrefs.categories[cat.id] ? 'bg-[#0044CC]' : 'bg-gray-200'
                      }`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                        notifPrefs.categories[cat.id] ? 'translate-x-6' : 'translate-x-1'
                      }`} />
                    </button>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card className="border-gray-200">
              <CardHeader>
                <CardTitle className="text-base">Ruhezeiten &amp; Häufigkeit</CardTitle>
                <CardDescription>Keine Benachrichtigungen in dieser Zeit</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm">Ruhephase von</Label>
                    <Input
                      type="time"
                      value={notifPrefs.quiet_hours_start}
                      onChange={e => setNotifPrefs(p => ({ ...p, quiet_hours_start: e.target.value }))}
                      className="mt-1"
                      data-testid="provider-quiet-hours-start"
                    />
                  </div>
                  <div>
                    <Label className="text-sm">Ruhephase bis</Label>
                    <Input
                      type="time"
                      value={notifPrefs.quiet_hours_end}
                      onChange={e => setNotifPrefs(p => ({ ...p, quiet_hours_end: e.target.value }))}
                      className="mt-1"
                      data-testid="provider-quiet-hours-end"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-sm">Max. Benachrichtigungen pro Tag</Label>
                  <Select
                    value={String(notifPrefs.max_per_day)}
                    onValueChange={v => setNotifPrefs(p => ({ ...p, max_per_day: Number(v) }))}
                  >
                    <SelectTrigger className="mt-1" data-testid="provider-max-per-day-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[1,2,3,4,5].map(n => (
                        <SelectItem key={n} value={String(n)}>{n} pro Tag</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm">Bevorzugte Sprache</Label>
                  <Select
                    value={notifPrefs.language}
                    onValueChange={v => setNotifPrefs(p => ({ ...p, language: v }))}
                  >
                    <SelectTrigger className="mt-1" data-testid="provider-language-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="de">Deutsch</SelectItem>
                      <SelectItem value="en">English</SelectItem>
                      <SelectItem value="fr">Français</SelectItem>
                      <SelectItem value="it">Italiano</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            <Button
              onClick={handleNotifSave}
              disabled={notifLoading}
              className="w-full bg-[#0044CC] hover:bg-[#003399] rounded-full"
              data-testid="provider-save-notif-btn"
            >
              {notifLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
              {t('save')}
            </Button>
          </div>
        </TabsContent>

        {/* Security Tab */}
        <TabsContent value="security">
          <Card className="border-gray-200">
            <CardHeader>
              <CardTitle>Passwort ändern</CardTitle>
              <CardDescription>Halten Sie Ihr Konto sicher</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handlePasswordChange} className="space-y-4">
                <div>
                  <Label>Aktuelles Passwort</Label>
                  <div className="relative mt-1">
                    <Input type={showCurrentPassword ? "text" : "password"} value={passwordData.currentPassword}
                      onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                      className="pr-10" data-testid="current-password-input" />
                    <button type="button" onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                      {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div>
                  <Label>Neues Passwort</Label>
                  <div className="relative mt-1">
                    <Input type={showNewPassword ? "text" : "password"} value={passwordData.newPassword}
                      onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                      className="pr-10" data-testid="new-password-input" />
                    <button type="button" onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                      {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div>
                  <Label>Passwort bestätigen</Label>
                  <Input type="password" value={passwordData.confirmPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                    className="mt-1" data-testid="confirm-password-input" />
                </div>
                <Button type="submit" className="w-full bg-[#0044CC] hover:bg-[#003399] rounded-full"
                  disabled={passwordLoading} data-testid="change-password-btn">
                  {passwordLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Passwort ändern'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Data Tab */}
        <TabsContent value="data">
          <div className="space-y-4">
            <Card className="border-gray-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Download className="w-5 h-5 text-[#0044CC]" /> Daten herunterladen
                </CardTitle>
                <CardDescription>DSGVO Art. 15 - Auskunftsrecht</CardDescription>
              </CardHeader>
              <CardContent>
                <Button onClick={handleDownloadData} variant="outline" className="w-full border-[#0044CC] text-[#0044CC] rounded-full"
                  disabled={downloadLoading} data-testid="download-data-btn">
                  {downloadLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}
                  Alle meine Daten herunterladen
                </Button>
              </CardContent>
            </Card>

            <Card className="border-red-200 bg-red-50/50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-red-600">
                  <AlertTriangle className="w-5 h-5" /> Konto löschen
                </CardTitle>
                <CardDescription className="text-red-600/70">Unwiderrufliche Aktion</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600 mb-4">
                  Durch das Löschen werden alle Daten, Bewertungen und Nachrichten endgültig entfernt.
                </p>
                <Button onClick={() => setShowDeleteConfirm(true)} variant="destructive" className="w-full rounded-full"
                  data-testid="delete-account-btn">
                  <Trash2 className="w-4 h-4 mr-2" /> Konto löschen
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" /> Konto wirklich löschen?
            </DialogTitle>
            <DialogDescription>
              Alle Ihre Daten werden unwiderruflich gelöscht.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowDeleteConfirm(false)} className="rounded-full">
              Abbrechen
            </Button>
            <Button variant="destructive" onClick={handleDeleteAccount} disabled={deleteLoading} className="rounded-full"
              data-testid="confirm-delete-btn">
              {deleteLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Ja, Konto löschen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SettingsPage;
